import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import CreateModal, { CreateModalProps } from "./index";
import { AiAssignmentCreateRequestDtoAssignmentCategory } from "@packages/api/madlenAPI.schemas";
import { Button } from "@core/index";
import { useToast } from "@packages/contexts/ToastContext";

interface AiAssignmentDesignerCreateModalProps
  extends Omit<CreateModalProps, "onSubmit"> {
  onSubmit: (input: any) => Promise<void>;
}

const AiAssignmentDesignerSpecificFields: React.FC<{
  assignmentCategory:
    | AiAssignmentCreateRequestDtoAssignmentCategory
    | undefined;
  setAssignmentCategory: (
    category: AiAssignmentCreateRequestDtoAssignmentCategory,
  ) => void;
}> = ({ assignmentCategory, setAssignmentCategory }) => {
  const { t } = useTranslation();

  const categories = [
    AiAssignmentCreateRequestDtoAssignmentCategory.GREEN_LIGHT,
    AiAssignmentCreateRequestDtoAssignmentCategory.RED_LIGHT_GREEN_LIGHT,
    AiAssignmentCreateRequestDtoAssignmentCategory.HUMAN_CRITIQUE_AI,
    AiAssignmentCreateRequestDtoAssignmentCategory.AI_CRITIQUE_HUMAN,
    AiAssignmentCreateRequestDtoAssignmentCategory.CO_CREATION,
  ];

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label
          className={`inline-block text-sm font-medium text-amber-700 dark:text-amber-300`}
        >
          {t("aiAssignmentDesigner.form.assignmentCategory")}
        </label>
        <div className="grid grid-cols-1 gap-3">
          {categories.map((category) => (
            <div key={category} className="space-y-2">
              <Button
                data-testid={`category-option-${category}`}
                type="button"
                onClick={() => setAssignmentCategory(category)}
                variant="solid"
                size="md"
                rounded="lg"
                fullWidth
                textColor={
                  assignmentCategory === category
                    ? "text-white"
                    : "text-gray-600 dark:text-gray-400"
                }
                bgColor={
                  assignmentCategory === category
                    ? `bg-gradient-to-r from-amber-600 to-yellow-600 dark:from-amber-400 dark:to-yellow-400 shadow-lg`
                    : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                }
                className="text-left p-4"
              >
                <div>
                  <div className="font-medium">
                    {t(
                      `aiAssignmentDesigner.assignmentCategoryOptions.${category}`,
                    )}
                  </div>
                  <div className="text-xs mt-1 opacity-80">
                    {t(
                      `aiAssignmentDesigner.assignmentCategoryOptions.${category}_description`,
                    )}
                  </div>
                </div>
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const AiAssignmentDesignerCreateModal: React.FC<
  AiAssignmentDesignerCreateModalProps
> = ({ onSubmit, ...props }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [assignmentCategory, setAssignmentCategory] = useState<
    AiAssignmentCreateRequestDtoAssignmentCategory | undefined
  >((props.initialInput as any).assignmentCategory);

  const handleSubmit = async (input: any) => {
    // Validate assignment category
    if (!assignmentCategory) {
      addToast({
        title: t("aiAssignmentDesigner.error.emptyCategory"),
        variant: "error",
        duration: 3000,
      });
      return;
    }

    // Prepare submission data with assignment category
    const submissionData = {
      ...input,
      assignmentCategory,
    };

    await onSubmit(submissionData);
  };

  return (
    <CreateModal
      {...props}
      themeColor="amber"
      onSubmit={handleSubmit}
      after={
        <AiAssignmentDesignerSpecificFields
          assignmentCategory={assignmentCategory}
          setAssignmentCategory={setAssignmentCategory}
        />
      }
    />
  );
};

export default AiAssignmentDesignerCreateModal;
